"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { SafeUser } from "@/app/types";
import toast from "react-hot-toast";
import axios from "axios";
import { Coffee } from "lucide-react";
import { useSession } from "next-auth/react";
import Link from "next/link";

interface PricingProps {
  currentUser?: SafeUser | null;
}

interface PricingPlan {
  name: string;
  displayName: string;
  price: number;
  monthlyEquivalent: number;
  originalMonthlyPrice: number;
  articles: number;
  description: string;
  features: string[];
  highlight: boolean;
  badge?: string;
}

const pricingPlans: PricingPlan[] = [
  {
    name: "basic-monthly",
    displayName: "Basic",
    price: 19.99,
    monthlyEquivalent: 19.99,
    originalMonthlyPrice: 19.99,
    articles: 20,
    description: "Perfect for startups",
    features: [
      "20 Articles/Month",
      "2 Websites",
      "SEO Dashboard",
      "AI-optimized for Google & AI search",
      "Backlink from the SEOZast directory",
      "Real-Time Analytics",
      "Automated backlinks",
      "One-Click GitHub Publishing",
    ],
    highlight: false,
  },
  {
    name: "pro-monthly",
    displayName: "Pro",
    price: 29.99,
    monthlyEquivalent: 29.99,
    originalMonthlyPrice: 29.99,
    articles: 40,
    description: "Great for growing sites",
    features: [
      "40 Articles/Month",
      "4 Websites",
      "SEO Dashboard",
      "AI-optimized for Google & AI search",
      "Backlink from the SEOZast directory",
      "Real-Time Analytics",
      "Automated backlinks",
      "One-Click GitHub Publishing",
      "Priority Support",
    ],
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "enterprise-monthly",
    displayName: "Enterprise",
    price: 59.99,
    monthlyEquivalent: 59.99,
    originalMonthlyPrice: 59.99,
    articles: 100,
    description: "Built for high-traffic sites",
    features: [
      "100 Articles/Month",
      "6 Websites",
      "SEO Dashboard",
      "AI-optimized for Google & AI search",
      "Backlink from the SEOZast directory",
      "Real-Time Analytics",
      "Automated backlinks",
      "One-Click GitHub Publishing",
      "Priority Support",
    ],
    highlight: true,
    badge: "Best for Scale",
  },
  {
    name: "basic-yearly",
    displayName: "Basic",
    price: 199.99,
    monthlyEquivalent: 16.66,
    originalMonthlyPrice: 19.99,
    articles: 20,
    description: "Perfect for startups",
    features: [
      "20 Articles/Month",
      "2 Websites",
      "SEO Dashboard",
      "AI-optimized for Google & AI search",
      "Backlink from the SEOZast directory",
      "Real-Time Analytics",
      "Automated backlinks",
      "One-Click GitHub Publishing",
    ],
    highlight: false,
  },
  {
    name: "pro-yearly",
    displayName: "Pro",
    price: 299.99,
    monthlyEquivalent: 24.99,
    originalMonthlyPrice: 29.99,
    articles: 40,
    description: "Great for growing sites",
    features: [
      "40 Articles/Month",
      "4 Websites",
      "SEO Dashboard",
      "AI-optimized for Google & AI search",
      "Backlink from the SEOZast directory",
      "Real-Time Analytics",
      "Automated backlinks",
      "One-Click GitHub Publishing",
      "Priority Support",
    ],
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "enterprise-yearly",
    displayName: "Enterprise",
    price: 599.99,
    monthlyEquivalent: 49.99,
    originalMonthlyPrice: 59.99,
    articles: 100,
    description: "Built for high-traffic sites",
    features: [
      "100 Articles/Month",
      "6 Websites",
      "SEO Dashboard",
      "AI-optimized for Google & AI search",
      "Backlink from the SEOZast directory",
      "Real-Time Analytics",
      "Automated backlinks",
      "One-Click GitHub Publishing",
      "Priority Support",
    ],
    highlight: true,
    badge: "Best for Scale",
  },
];

const PUBLIC_COUPONS: Record<string, {
  discountType: 'PERCENTAGE' | 'FIXED';
  discountValue: number;
  message: string;
  eligiblePlans?: string[];
}> = {
  'BFCM2025': {
    discountType: 'PERCENTAGE',
    discountValue: 30,
    message: '🔥 30% OFF Black Friday Deal applied!',
    eligiblePlans: ['Pro', 'Enterprise']
  },
  'BF2025': {
    discountType: 'PERCENTAGE',
    discountValue: 30,
    message: '🔥 30% OFF Black Friday Deal applied!'
  },
};

const PricingSection: React.FC<PricingProps> = ({ currentUser }) => {
  const { data: session } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isYearly, setIsYearly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; valid: boolean; message?: string; discountType?: string; discountValue?: number; } | null>(null);
  const [referralCode, setReferralCode] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Manejar referral code con persistencia en localStorage
    const ref = searchParams?.get("ref")?.toString();
    if (ref) {
      // Si hay ref en URL, guardarla en localStorage
      localStorage.setItem('seozast_referral', ref);
      setReferralCode(ref);
    } else {
      // Si no hay ref en URL, intentar cargarla desde localStorage
      const storedRef = localStorage.getItem('seozast_referral');
      if (storedRef) {
        setReferralCode(storedRef);
      }
    }

    const couponFromUrl = searchParams?.get('coupon');
    if (couponFromUrl && !appliedCoupon) {
      const upperCode = couponFromUrl.toUpperCase();
      setCouponCode(upperCode);

      if (PUBLIC_COUPONS[upperCode]) {
        const coupon = PUBLIC_COUPONS[upperCode];
        setAppliedCoupon({
          code: upperCode,
          valid: true,
          message: coupon.message,
          discountType: coupon.discountType,
          discountValue: coupon.discountValue
        });
        toast.success(coupon.message, { duration: 4000 });
      } else if (session) {
        handleApplyCouponAuto(upperCode);
      }
    }
  }, [searchParams, session]);

  const handlePlanSelection = async (planName: string) => {
    const planKey = `${planName.toLowerCase()}${isYearly ? "-yearly" : "-monthly"}`;

    if (!session) {
      router.push(`/register?plan=${planName.toLowerCase()}&yearly=${isYearly}`);
    } else {
      await handleBecomePremium(planKey);
    }
  };

  const handleBecomePremium = async (selectedPlan: string) => {
    if (!session?.user.id) {
      toast.error("User not identified");
      return;
    }

    setLoading(true);
    try {
      const payload: { plan: string; couponCode?: string, referralCode?: string } = { plan: selectedPlan };
      if (appliedCoupon?.valid && couponCode) {
        payload.couponCode = couponCode;
      }

      if (referralCode) {
        payload.referralCode = referralCode;
      }

      const response = await axios.post("/api/stripe/subscribe", payload);
      if (response.data.url) {
        toast.success("Redirecting to payment page...");
        setTimeout(() => {
          window.location.href = response.data.url;
        }, 1000);
      } else {
        toast.success(response.data.message);
        // Limpiar referral code después de suscripción exitosa
        localStorage.removeItem('seozast_referral');
        router.refresh();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Error initiating subscription");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCouponAuto = async (code: string) => {
    if (!code.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post("/api/coupons/validate", { code });
      if (response.data.valid) {
        setAppliedCoupon({
          code,
          valid: true,
          message: response.data.message,
          discountType: response.data.discountType,
          discountValue: response.data.discountValue
        });
        toast.success(response.data.message || 'Coupon applied!');
      }
    } catch (error: any) {
      console.log("Coupon validation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Enter a coupon code");
      return;
    }

    const upperCode = couponCode.toUpperCase();

    if (PUBLIC_COUPONS[upperCode]) {
      const coupon = PUBLIC_COUPONS[upperCode];
      setAppliedCoupon({
        code: upperCode,
        valid: true,
        message: coupon.message,
        discountType: coupon.discountType,
        discountValue: coupon.discountValue
      });
      toast.success(coupon.message);
      return;
    }

    if (!session) {
      toast.error("Please sign up or log in to use this coupon");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post("/api/coupons/validate", { code: upperCode });
      if (response.data.valid) {
        setAppliedCoupon({
          code: upperCode,
          valid: true,
          message: response.data.message,
          discountType: response.data.discountType,
          discountValue: response.data.discountValue
        });
        toast.success("Coupon applied successfully!");
      } else {
        setAppliedCoupon({ code: upperCode, valid: false, message: response.data.error });
        toast.error(response.data.error || "Invalid coupon");
      }
    } catch (error: any) {
      setAppliedCoupon({ code: upperCode, valid: false });
      toast.error(error.response?.data?.error || "Failed to validate coupon");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setAppliedCoupon(null);
    toast.success("Coupon removed");
  };

  const formatPrice = (price: number) => {
    const fixedPrice = price.toFixed(2);
    const [dollars, cents] = fixedPrice.split('.');
    return (
      <>
        {dollars}
        <span className="text-lg align-top">.{cents}</span>
      </>
    );
  };

  const isPlanEligibleForDiscount = (planName: string): boolean => {
    if (!appliedCoupon?.valid) return false;

    const upperCode = appliedCoupon.code.toUpperCase();
    const publicCoupon = PUBLIC_COUPONS[upperCode];

    if (publicCoupon?.eligiblePlans) {
      return publicCoupon.eligiblePlans.includes(planName);
    }

    return true;
  };

  const getDisplayPrice = (plan: any): JSX.Element => {
    let displayElements: JSX.Element[] = [];

    const shouldApplyDiscount = appliedCoupon?.valid && isPlanEligibleForDiscount(plan.displayName);

    if (shouldApplyDiscount) {
      let discountType = appliedCoupon.discountType || "PERCENTAGE";
      let discountValue: number = appliedCoupon.discountValue ? Number(appliedCoupon.discountValue) : 0;

      if (isYearly) {
        let discountedAnnualPrice = plan.price;

        if (discountType === "PERCENTAGE" && discountValue > 0) {
          discountedAnnualPrice = plan.price * (1 - discountValue / 100);
        } else if (discountType === "FIXED" && discountValue > 0) {
          discountedAnnualPrice = Math.max(0, plan.price - discountValue);
        }

        const discountedMonthlyEquivalent = discountedAnnualPrice / 12;

        displayElements.push(
          <div key="yearly-display" className="space-y-1">
            <div className="text-3xl font-bold text-gray-800">
              <s className="text-xl text-gray-500 mr-2">
                ${formatPrice(plan.monthlyEquivalent)}
              </s>
              <span className="text-emerald-600">
                ${formatPrice(discountedMonthlyEquivalent)}
              </span>
              <span className="text-base font-normal text-gray-600">/mo*</span>
            </div>
            <div className="text-sm text-gray-500">
              Billed ${Math.round(discountedAnnualPrice)} annually
            </div>
            <div className="text-md font-light text-gray-800 line-through">
              ${Math.round(plan.originalMonthlyPrice)}
              <span className="text-gray-600">/mo</span>
            </div>
          </div>
        );
      } else {
        let discountedPrice = plan.price;

        if (discountType === "PERCENTAGE" && discountValue > 0) {
          discountedPrice = plan.price * (1 - discountValue / 100);
        } else if (discountType === "FIXED" && discountValue > 0) {
          discountedPrice = Math.max(0, plan.price - discountValue);
        }

        displayElements.push(
          <div key="monthly-display">
            <div className="text-3xl font-bold text-gray-800">
              <s className="text-xl text-gray-500 mr-2">
                ${formatPrice(plan.price)}
              </s>
              <span className="text-emerald-600">
                ${formatPrice(discountedPrice)}
              </span>
              <span className="text-base font-normal text-gray-600">/mo</span>
            </div>
          </div>
        );
      }
    } else {
      if (isYearly) {
        displayElements.push(
          <div key="yearly-no-coupon" className="space-y-1">
            <div className="text-3xl font-bold text-gray-800">
              ${formatPrice(plan.monthlyEquivalent)}
              <span className="text-base font-normal text-gray-600">/mo*</span>
            </div>
            <div className="text-sm text-gray-500">
              Billed ${Math.round(plan.price)} annually
            </div>
            <div className="text-md font-light text-gray-800 line-through">
              ${Math.round(plan.originalMonthlyPrice)}
              <span className="text-gray-600">/mo</span>
            </div>
          </div>
        );
      } else {
        displayElements.push(
          <div key="monthly-no-coupon">
            <div className="text-3xl font-bold text-gray-800">
              ${formatPrice(plan.price)}
              <span className="text-base font-normal text-gray-600">/mo</span>
            </div>
          </div>
        );
      }
    }

    return <>{displayElements}</>;
  };

  return (
    <section id="pricing" className="bg-gray-100 py-10 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {appliedCoupon?.valid && (
          <div className="mb-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-center py-3 px-4 rounded-lg shadow-lg">
            <p className="font-bold text-lg">🔥 Black Friday Deal Active!</p>
            <p className="text-sm">
              {appliedCoupon.message} - Limited time offer
            </p>
          </div>
        )}
        {/* {referralCode && (
          <div className="mb-6 bg-emerald-100 border-2 border-emerald-300 text-emerald-800 text-center py-3 px-4 rounded-lg shadow-md">
            <p className="font-semibold text-sm">
              🎉 You're using a referral link! Your affiliate will earn commission when you subscribe.
            </p>
          </div>
        )} */}
        <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-8">
          Start Growing Your Traffic Today
        </h2>
        <h4 className="text-xl md:text-lg text-black font-medium text-center mb-8">
          Everything you need for SEO and AI search — 3× cheaper than Surfer, Jasper, or Frase.
        </h4>
        <div className="flex justify-center mb-12">
          <div className="flex w-full sm:w-auto bg-white rounded-lg shadow-sm border">
            <button
              onClick={() => setIsYearly(false)}
              className={`flex-1 px-4 sm:px-6 py-3 text-sm sm:text-base font-medium transition-all focus:outline-none focus:ring-2 focus:ring-emerald-600 rounded-l-lg ${!isYearly
                ? "bg-emerald-600 text-white shadow-md"
                : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`flex-1 px-4 sm:px-6 py-3 text-sm sm:text-base font-medium transition-all focus:outline-none focus:ring-2 focus:ring-emerald-600 relative rounded-r-lg ${isYearly
                ? "bg-emerald-600 text-white shadow-md"
                : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              Yearly
              <span className="absolute -top-3 -right-2 bg-yellow-400 text-black text-[10px] font-bold px-2 py-1 rounded-full shadow-sm z-10">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {pricingPlans
            .filter((plan) =>
              isYearly ? plan.name.includes("-yearly") : plan.name.includes("-monthly")
            )
            .map((plan) => (
              <div
                key={plan.name}
                className={`relative bg-white p-6 rounded-lg border shadow-md text-center flex flex-col min-h-[400px] transition-all duration-300 ${plan.displayName === "Pro"
                  ? "border-2 border-emerald-600"
                  : plan.highlight
                    ? "border-gray-200 bg-emerald-50"
                    : "border-gray-200"
                  } ${loading ? "opacity-70 cursor-not-allowed" : "hover:shadow-lg"}`}
              >
                {plan.badge && (
                  <div className="absolute top-0 left-0 right-0 flex justify-center -translate-y-1/2 z-30">
                    <span className="text-xs font-bold px-3 py-1 rounded-full shadow-sm bg-emerald-100 text-emerald-500">
                      {plan.badge}
                    </span>
                  </div>
                )}
                {isYearly && (
                  <span className="absolute top-6 right-2 bg-yellow-300 text-black text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm z-20 flex items-center">
                    <Coffee className="w-3 h-3 mr-1" />
                    Save ${plan.name === "basic-yearly" && "12"}{plan.name === "pro-yearly" && "20"}{plan.name === "enterprise-yearly" && "60"}
                  </span>
                )}
                <h3
                  className={`text-xl font-semibold text-gray-800 mb-3 pt-4 ${plan.badge ? "mt-6" : ""
                    }`}
                >
                  {plan.displayName}
                </h3>
                <div className="mb-2">{getDisplayPrice(plan)}</div>
                <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                <ul className="text-left text-base text-gray-700 space-y-2 mb-6 flex-grow">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-emerald-600 mr-2">✔</span>
                      <div>
                        <span>{feature}</span>
                        {feature === "Automated backlinks" && (
                          <p className="text-xs text-gray-500 ml-2">
                            (from your published articles on your site)
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handlePlanSelection(plan.displayName)}
                  disabled={loading}
                  className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${loading
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-emerald-600 text-white hover:bg-emerald-500"
                    }`}
                >
                  {loading ? "Processing..." : session ? "Subscribe" : "Sign Up"}
                </button>
              </div>
            ))}
        </div>

        <div className="mt-8">
          <p className="sm:text-lg text-md text-center mb-4 font-semibold">Do you have a coupon?</p>
          <p className="sm:text-md text-sm text-center mb-4 font-light">Sign up/Log in to apply it</p>

          <div className="max-w-md mx-auto mb-8 flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter coupon code"
              className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
              disabled={loading}
            />
            {appliedCoupon?.valid ? (
              <button
                onClick={handleRemoveCoupon}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                disabled={loading}
              >
                Remove
              </button>
            ) : (
              <button
                onClick={handleApplyCoupon}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                disabled={loading}
              >
                Apply Coupon
              </button>
            )}
          </div>
        </div>
        {appliedCoupon && (
          <div className="text-center mb-4 text-sm">
            {appliedCoupon.valid ? (
              <span className="text-emerald-600 font-medium">{appliedCoupon.message || `${appliedCoupon.code} applied!`}</span>
            ) : (
              <span className="text-red-600">{appliedCoupon.message || "Invalid coupon"}</span>
            )}
          </div>
        )}

        <div className="mt-16 text-center">
          <div className="inline-block bg-emerald-100 text-black font-medium sm:text-lg text-md px-4 py-2 rounded-lg shadow-md transition-transform duration-300 hover:scale-105 border border-emerald-300">
            Pair with{' '}
            <Link
              href="https://peerquik.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-500 hover:underline font-medium"
            >
              PeerQuik
            </Link>{' '}
            for rapid B2B/B2C launches.
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in forwards;
        }
      `}</style>
    </section>
  );
};

export default PricingSection;

// "use client";

// import { useEffect, useState } from "react";
// import { useRouter, useSearchParams } from "next/navigation";
// import { SafeUser } from "@/app/types";
// import toast from "react-hot-toast";
// import axios from "axios";
// import { Coffee } from "lucide-react";
// import { useSession } from "next-auth/react";
// import Link from "next/link";

// interface PricingProps {
//   currentUser?: SafeUser | null;
// }

// interface PricingPlan {
//   name: string;
//   displayName: string;
//   price: number;
//   monthlyEquivalent: number;
//   originalMonthlyPrice: number;
//   articles: number;
//   description: string;
//   features: string[];
//   highlight: boolean;
//   badge?: string;
// }

// const pricingPlans: PricingPlan[] = [
//   {
//     name: "basic-monthly",
//     displayName: "Basic",
//     price: 19.99,
//     monthlyEquivalent: 19.99,
//     originalMonthlyPrice: 19.99,
//     articles: 20,
//     description: "Perfect for startups",
//     features: [
//       "20 Articles/Month",
//       "2 Websites",
//       "SEO Dashboard",
//       "AI-optimized for Google & AI search",
//       "Backlink from the SEOZast directory",
//       "Real-Time Analytics",
//       "Automated backlinks",
//       "One-Click GitHub Publishing",
//     ],
//     highlight: false,
//   },
//   {
//     name: "pro-monthly",
//     displayName: "Pro",
//     price: 29.99,
//     monthlyEquivalent: 29.99,
//     originalMonthlyPrice: 29.99,
//     articles: 40,
//     description: "Great for growing sites",
//     features: [
//       "40 Articles/Month",
//       "4 Websites",
//       "SEO Dashboard",
//       "AI-optimized for Google & AI search",
//       "Backlink from the SEOZast directory",
//       "Real-Time Analytics",
//       "Automated backlinks",
//       "One-Click GitHub Publishing",
//       "Priority Support",
//     ],
//     highlight: true,
//     badge: "Most Popular",
//   },
//   {
//     name: "enterprise-monthly",
//     displayName: "Enterprise",
//     price: 59.99,
//     monthlyEquivalent: 59.99,
//     originalMonthlyPrice: 59.99,
//     articles: 100,
//     description: "Built for high-traffic sites",
//     features: [
//       "100 Articles/Month",
//       "6 Websites",
//       "SEO Dashboard",
//       "AI-optimized for Google & AI search",
//       "Backlink from the SEOZast directory",
//       "Real-Time Analytics",
//       "Automated backlinks",
//       "One-Click GitHub Publishing",
//       "Priority Support",
//     ],
//     highlight: true,
//     badge: "Best for Scale",
//   },
//   {
//     name: "basic-yearly",
//     displayName: "Basic",
//     price: 199.99, // ~17% savings from monthly (5.99 × 12 = 71.88)
//     monthlyEquivalent: 16.66,
//     originalMonthlyPrice: 19.99,
//     articles: 20,
//     description: "Perfect for startups",
//     features: [
//       "20 Articles/Month",
//       "2 Websites",
//       "SEO Dashboard",
//       "AI-optimized for Google & AI search",
//       "Backlink from the SEOZast directory",
//       "Real-Time Analytics",
//       "Automated backlinks",
//       "One-Click GitHub Publishing",
//     ],
//     highlight: false,
//   },
//   {
//     name: "pro-yearly",
//     displayName: "Pro",
//     price: 299.99, // ~17% savings from monthly (9.99 × 12 = 119.88)
//     monthlyEquivalent: 24.99,
//     originalMonthlyPrice: 29.99,
//     articles: 40,
//     description: "Great for growing sites",
//     features: [
//       "40 Articles/Month",
//       "4 Websites",
//       "SEO Dashboard",
//       "AI-optimized for Google & AI search",
//       "Backlink from the SEOZast directory",
//       "Real-Time Analytics",
//       "Automated backlinks",
//       "One-Click GitHub Publishing",
//       "Priority Support",
//     ],
//     highlight: true,
//     badge: "Most Popular",
//   },
//   {
//     name: "enterprise-yearly",
//     displayName: "Enterprise",
//     price: 599.99, // ~17% savings from monthly (29.99 × 12 = 359.88)
//     monthlyEquivalent: 49.99,
//     originalMonthlyPrice: 59.99,
//     articles: 100,
//     description: "Built for high-traffic sites",
//     features: [
//       "100 Articles/Month",
//       "6 Websites",
//       "SEO Dashboard",
//       "AI-optimized for Google & AI search",
//       "Backlink from the SEOZast directory",
//       "Real-Time Analytics",
//       "Automated backlinks",
//       "One-Click GitHub Publishing",
//       "Priority Support",
//     ],
//     highlight: true,
//     badge: "Best for Scale",
//   },
// ];

// const PUBLIC_COUPONS: Record<string, {
//   discountType: 'PERCENTAGE' | 'FIXED';
//   discountValue: number;
//   message: string;
// }> = {
//   'BFCM2025': {
//     discountType: 'PERCENTAGE',
//     discountValue: 30,
//     message: '🔥 30% OFF Black Friday Deal applied!'
//   },
//   'BF2025': {
//     discountType: 'PERCENTAGE',
//     discountValue: 30,
//     message: '🔥 30% OFF Black Friday Deal applied!'
//   },
//   // Añade más cupones según necesites
// };

// const PricingSection: React.FC<PricingProps> = ({ currentUser }) => {
//   const { data: session } = useSession();
//   const router = useRouter();
//   const searchParams = useSearchParams();
//   const [isYearly, setIsYearly] = useState(true);
//   const [loading, setLoading] = useState(false);
//   const [couponCode, setCouponCode] = useState(""); // Estado global para el código de cupón
//   const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; valid: boolean; message?: string; discountType?: string; discountValue?: number; } | null>(null); // Estado para cupón aplicado

//   useEffect(() => {
//     const couponFromUrl = searchParams?.get('coupon');
//     if (couponFromUrl && !appliedCoupon) {
//       const upperCode = couponFromUrl.toUpperCase();
//       setCouponCode(upperCode);

//       // Valida si es un cupón público conocido
//       if (PUBLIC_COUPONS[upperCode]) {
//         const coupon = PUBLIC_COUPONS[upperCode];
//         setAppliedCoupon({
//           code: upperCode,
//           valid: true,
//           message: coupon.message,
//           discountType: coupon.discountType,
//           discountValue: coupon.discountValue
//         });
//         toast.success(coupon.message, { duration: 4000 });
//       } else if (session) {
//         // Si está logueado, valida con el backend
//         handleApplyCouponAuto(upperCode);
//       }
//     }
//   }, [searchParams, session]);

//   const handlePlanSelection = async (planName: string) => {
//     const planKey = `${planName.toLowerCase()}${isYearly ? "-yearly" : "-monthly"}`;
//     // console.log("planKey", planKey);

//     if (!session) {
//       router.push(`/register?plan=${planName.toLowerCase()}&yearly=${isYearly}`);
//     } else {
//       await handleBecomePremium(planKey);
//     }
//   };

//   const handleBecomePremium = async (selectedPlan: string) => {
//     if (!session?.user.id) {
//       toast.error("User not identified");
//       return;
//     }

//     setLoading(true);
//     try {
//       // Envía el couponCode si está aplicado y válido
//       const payload: { plan: string; couponCode?: string } = { plan: selectedPlan };
//       if (appliedCoupon?.valid && couponCode) {
//         payload.couponCode = couponCode;
//       }

//       const response = await axios.post("/api/stripe/subscribe", payload);
//       if (response.data.url) {
//         toast.success("Redirecting to payment page...");
//         setTimeout(() => {
//           window.location.href = response.data.url;
//         }, 1000);
//       } else {
//         toast.success(response.data.message);
//         router.refresh();
//       }
//     } catch (error: any) {
//       toast.error(error.response?.data?.error || "Error initiating subscription");
//       console.error(error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleApplyCouponAuto = async (code: string) => {
//     if (!code.trim()) return;
//     setLoading(true);
//     try {
//       const response = await axios.post("/api/coupons/validate", { code });
//       if (response.data.valid) {
//         setAppliedCoupon({
//           code,
//           valid: true,
//           message: response.data.message,
//           discountType: response.data.discountType,
//           discountValue: response.data.discountValue
//         });
//         toast.success(response.data.message || 'Coupon applied!');
//       }
//     } catch (error: any) {
//       console.log("Coupon validation failed:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleApplyCoupon = async () => {
//     if (!couponCode.trim()) {
//       toast.error("Enter a coupon code");
//       return;
//     }

//     const upperCode = couponCode.toUpperCase();

//     // Primero verifica si es un cupón público
//     if (PUBLIC_COUPONS[upperCode]) {
//       const coupon = PUBLIC_COUPONS[upperCode];
//       setAppliedCoupon({
//         code: upperCode,
//         valid: true,
//         message: coupon.message,
//         discountType: coupon.discountType,
//         discountValue: coupon.discountValue
//       });
//       toast.success(coupon.message);
//       return;
//     }

//     // Si no es público y el usuario está logueado, valida en backend
//     if (!session) {
//       toast.error("Please sign up or log in to use this coupon");
//       return;
//     }

//     setLoading(true);
//     try {
//       const response = await axios.post("/api/coupons/validate", { code: upperCode });
//       if (response.data.valid) {
//         setAppliedCoupon({
//           code: upperCode,
//           valid: true,
//           message: response.data.message,
//           discountType: response.data.discountType,
//           discountValue: response.data.discountValue
//         });
//         toast.success("Coupon applied successfully!");
//       } else {
//         setAppliedCoupon({ code: upperCode, valid: false, message: response.data.error });
//         toast.error(response.data.error || "Invalid coupon");
//       }
//     } catch (error: any) {
//       setAppliedCoupon({ code: upperCode, valid: false });
//       toast.error(error.response?.data?.error || "Failed to validate coupon");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // const handleApplyCoupon = async () => {
//   //   if (!couponCode.trim()) {
//   //     toast.error("Enter a coupon code");
//   //     return;
//   //   }

//   //   setLoading(true);
//   //   try {
//   //     // Opcional: Valida el cupón en el backend para seguridad (evita spoofing)
//   //     // Aquí solo validamos localmente, pero el backend lo validará de nuevo
//   //     const response = await axios.post("/api/coupons/validate", { code: couponCode }); // Crea este endpoint si quieres validar aquí
//   //     if (response.data.valid) {
//   //       setAppliedCoupon({ code: couponCode, valid: true, message: response.data.message || "Coupon applied!" });
//   //       toast.success("Coupon applied successfully!");
//   //     } else {
//   //       setAppliedCoupon({ code: couponCode, valid: false, message: response.data.error });
//   //       toast.error(response.data.error || "Invalid coupon");
//   //     }
//   //   } catch (error: any) {
//   //     setAppliedCoupon({ code: couponCode, valid: false });
//   //     toast.error(error.response?.data?.error || "Failed to validate coupon");
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   const handleRemoveCoupon = () => {
//     setCouponCode("");
//     setAppliedCoupon(null);
//     toast.success("Coupon removed");
//   };


//   const formatPrice = (price: number) => {
//     const fixedPrice = price.toFixed(2);
//     const [dollars, cents] = fixedPrice.split('.');
//     return (
//       <>
//         {dollars}
//         <span className="text-lg align-top">.{cents}</span>
//       </>
//     );
//   };

//   const isPlanEligibleForDiscount = (planName: string): boolean => {
//     const eligiblePlans = ['Pro', 'Enterprise'];
//     return eligiblePlans.includes(planName);
//   };


//   const getDisplayPrice = (plan: any): JSX.Element => {
//     let displayElements: JSX.Element[] = [];

//     // 🎯 Solo aplicar descuento si el cupón es válido Y el plan es elegible
//     const shouldApplyDiscount = appliedCoupon?.valid && isPlanEligibleForDiscount(plan.displayName);

//     if (shouldApplyDiscount) {
//       let discountType = appliedCoupon.discountType || "PERCENTAGE";
//       let discountValue: number = appliedCoupon.discountValue ? Number(appliedCoupon.discountValue) : 0;

//       if (isYearly) {
//         // Para planes anuales: aplicar descuento al precio anual, luego calcular mensual
//         let discountedAnnualPrice = plan.price;

//         if (discountType === "PERCENTAGE" && discountValue > 0) {
//           discountedAnnualPrice = plan.price * (1 - discountValue / 100);
//         } else if (discountType === "FIXED" && discountValue > 0) {
//           discountedAnnualPrice = Math.max(0, plan.price - discountValue);
//         }

//         const discountedMonthlyEquivalent = discountedAnnualPrice / 12;

//         displayElements.push(
//           <div key="yearly-display" className="space-y-1">
//             <div className="text-3xl font-bold text-gray-800">
//               <s className="text-xl text-gray-500 mr-2">
//                 ${formatPrice(plan.monthlyEquivalent)}
//               </s>
//               <span className="text-emerald-600">
//                 ${formatPrice(discountedMonthlyEquivalent)}
//               </span>
//               <span className="text-base font-normal text-gray-600">/mo*</span>
//             </div>
//             <div className="text-sm text-gray-500">
//               Billed ${Math.round(discountedAnnualPrice)} annually
//             </div>
//             <div className="text-md font-light text-gray-800 line-through">
//               ${Math.round(plan.originalMonthlyPrice)}
//               <span className="text-gray-600">/mo</span>
//             </div>
//           </div>
//         );
//       } else {
//         // Para planes mensuales: aplicar descuento directo
//         let discountedPrice = plan.price;

//         if (discountType === "PERCENTAGE" && discountValue > 0) {
//           discountedPrice = plan.price * (1 - discountValue / 100);
//         } else if (discountType === "FIXED" && discountValue > 0) {
//           discountedPrice = Math.max(0, plan.price - discountValue);
//         }

//         displayElements.push(
//           <div key="monthly-display">
//             <div className="text-3xl font-bold text-gray-800">
//               <s className="text-xl text-gray-500 mr-2">
//                 ${formatPrice(plan.price)}
//               </s>
//               <span className="text-emerald-600">
//                 ${formatPrice(discountedPrice)}
//               </span>
//               <span className="text-base font-normal text-gray-600">/mo</span>
//             </div>
//           </div>
//         );
//       }
//     } else {
//       // Sin descuento o plan no elegible
//       if (isYearly) {
//         displayElements.push(
//           <div key="yearly-no-coupon" className="space-y-1">
//             <div className="text-3xl font-bold text-gray-800">
//               ${formatPrice(plan.monthlyEquivalent)}
//               <span className="text-base font-normal text-gray-600">/mo*</span>
//             </div>
//             <div className="text-sm text-gray-500">
//               Billed ${Math.round(plan.price)} annually
//             </div>
//             <div className="text-md font-light text-gray-800 line-through">
//               ${Math.round(plan.originalMonthlyPrice)}
//               <span className="text-gray-600">/mo</span>
//             </div>
//           </div>
//         );
//       } else {
//         displayElements.push(
//           <div key="monthly-no-coupon">
//             <div className="text-3xl font-bold text-gray-800">
//               ${formatPrice(plan.price)}
//               <span className="text-base font-normal text-gray-600">/mo</span>
//             </div>
//           </div>
//         );
//       }
//     }

//     return <>{displayElements}</>;
//   };

//   return (
//     <section id="pricing" className="bg-gray-100 py-10 sm:py-16">
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//         {appliedCoupon?.valid && (
//           <div className="mb-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-center py-3 px-4 rounded-lg shadow-lg">
//             <p className="font-bold text-lg">🔥 Black Friday Deal Active!</p>
//             <p className="text-sm">
//               {appliedCoupon.message} - Limited time offer
//             </p>
//           </div>
//         )}
//         <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-8">
//           Start Growing Your Traffic Today
//         </h2>
//         <h4 className="text-xl md:text-lg text-black font-medium text-center mb-8">
//           Everything you need for SEO and AI search — 3× cheaper than Surfer, Jasper, or Frase.
//         </h4>
//         {/* Toggle Section */}
//         <div className="flex justify-center mb-12">
//           <div className="flex w-full sm:w-auto bg-white rounded-lg shadow-sm border">
//             <button
//               onClick={() => setIsYearly(false)}
//               className={`flex-1 px-4 sm:px-6 py-3 text-sm sm:text-base font-medium transition-all focus:outline-none focus:ring-2 focus:ring-emerald-600 rounded-l-lg ${!isYearly
//                 ? "bg-emerald-600 text-white shadow-md"
//                 : "bg-white text-gray-700 hover:bg-gray-50"
//                 }`}
//             >
//               Monthly
//             </button>
//             <button
//               onClick={() => setIsYearly(true)}
//               className={`flex-1 px-4 sm:px-6 py-3 text-sm sm:text-base font-medium transition-all focus:outline-none focus:ring-2 focus:ring-emerald-600 relative rounded-r-lg ${isYearly
//                 ? "bg-emerald-600 text-white shadow-md"
//                 : "bg-white text-gray-700 hover:bg-gray-50"
//                 }`}
//             >
//               Yearly
//               <span className="absolute -top-3 -right-2 bg-yellow-400 text-black text-[10px] font-bold px-2 py-1 rounded-full shadow-sm z-10">
//                 Save 17%
//               </span>
//             </button>
//           </div>
//         </div>

//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
//           {pricingPlans
//             .filter((plan) =>
//               isYearly ? plan.name.includes("-yearly") : plan.name.includes("-monthly")
//             )
//             .map((plan) => (
//               <div
//                 key={plan.name}
//                 className={`relative bg-white p-6 rounded-lg border shadow-md text-center flex flex-col min-h-[400px] transition-all duration-300 ${plan.displayName === "Pro"
//                   ? "border-2 border-emerald-600"
//                   : plan.highlight
//                     ? "border-gray-200 bg-emerald-50"
//                     : "border-gray-200"
//                   } ${loading ? "opacity-70 cursor-not-allowed" : "hover:shadow-lg"}`}
//               >
//                 {plan.badge && (
//                   <div className="absolute top-0 left-0 right-0 flex justify-center -translate-y-1/2 z-30">
//                     <span className="text-xs font-bold px-3 py-1 rounded-full shadow-sm bg-emerald-100 text-emerald-500">
//                       {plan.badge}
//                     </span>
//                   </div>
//                 )}
//                 {isYearly && (
//                   <span className="absolute top-6 right-2 bg-yellow-300 text-black text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm z-20 flex items-center">
//                     <Coffee className="w-3 h-3 mr-1" />
//                     Save ${plan.name === "basic-yearly" && "12"}{plan.name === "pro-yearly" && "20"}{plan.name === "enterprise-yearly" && "60"}
//                   </span>
//                 )}
//                 <h3
//                   className={`text-xl font-semibold text-gray-800 mb-3 pt-4 ${plan.badge ? "mt-6" : ""
//                     }`}
//                 >
//                   {plan.displayName}
//                 </h3>
//                 <div className="mb-2">{getDisplayPrice(plan)}</div>
//                 <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
//                 <ul className="text-left text-base text-gray-700 space-y-2 mb-6 flex-grow">
//                   {plan.features.map((feature, idx) => (
//                     <li key={idx} className="flex items-start">
//                       <span className="text-emerald-600 mr-2">✔</span>
//                       <div>
//                         <span>{feature}</span>

//                         {feature === "Automated backlinks" && (
//                           <p className="text-xs text-gray-500 ml-2">
//                             (from your published articles on your site)
//                           </p>
//                         )}
//                       </div>
//                     </li>
//                   ))}
//                 </ul>
//                 <button
//                   onClick={() => handlePlanSelection(plan.displayName)}
//                   disabled={loading}
//                   className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${loading
//                     ? "bg-gray-300 text-gray-600 cursor-not-allowed"
//                     : "bg-emerald-600 text-white hover:bg-emerald-500"
//                     }`}
//                 >
//                   {loading ? "Processing..." : session ? "Subscribe" : "Sign Up"}
//                 </button>
//               </div>
//             ))}
//         </div>
//         {/* <p className="text-md text-gray-400 mt-8 text-center">
//           ⚡ Launch offer: lock this price for lifetime access.
//           Prices double next week.
//         </p> */}

//         {/* Coupon Input Global - Aplica a todos los planes */}
//         <div className="mt-8">
//           <p className="sm:text-lg text-md text-center mb-4 font-semibold">Do you have a coupon?</p>
//           <p className="sm:text-md text-sm text-center mb-4 font-light">Sign up/Log in to apply it</p>

//           <div className="max-w-md mx-auto mb-8 flex flex-col sm:flex-row gap-2">

//             <input
//               type="text"
//               value={couponCode}
//               onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
//               placeholder="Enter coupon code"
//               className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
//               disabled={loading}
//             />
//             {appliedCoupon?.valid ? (
//               <button
//                 onClick={handleRemoveCoupon}
//                 className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
//                 disabled={loading}
//               >
//                 Remove
//               </button>
//             ) : (
//               <button
//                 onClick={handleApplyCoupon}
//                 className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
//                 disabled={loading}
//               >
//                 Apply Coupon
//               </button>
//             )}
//           </div>
//         </div>
//         {appliedCoupon && (
//           <div className="text-center mb-4 text-sm">
//             {appliedCoupon.valid ? (
//               <span className="text-emerald-600 font-medium">{appliedCoupon.message || `${appliedCoupon.code} applied!`}</span>
//             ) : (
//               <span className="text-red-600">{appliedCoupon.message || "Invalid coupon"}</span>
//             )}
//           </div>
//         )}

//         <div className="mt-16 text-center">
//           <div className="inline-block bg-emerald-100 text-black font-medium sm:text-lg text-md px-4 py-2 rounded-lg shadow-md transition-transform duration-300 hover:scale-105 border border-emerald-300">
//             Pair with{' '}
//             <Link
//               href="https://peerquik.com"
//               target="_blank"
//               rel="noopener noreferrer"
//               className="text-emerald-500 hover:underline font-medium"
//             >
//               PeerQuik
//             </Link>{' '}
//             for rapid B2B/B2C launches.
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         @keyframes fadeIn {
//           from {
//             opacity: 0;
//           }
//           to
//             opacity: 1;
//           }
//         }
//         .animate-fadeIn {
//           animation: fadeIn 0.3s ease-in forwards;
//         }
//       `}</style>
//     </section>
//   );
// };

// export default PricingSection;

// // "use client";

// // import { useState } from "react";
// // import { useRouter } from "next/navigation";
// // import { SafeUser } from "@/app/types";
// // import toast from "react-hot-toast";
// // import axios from "axios";
// // import { Coffee } from "lucide-react";
// // import { useSession } from "next-auth/react";
// // import Link from "next/link";

// // interface PricingProps {
// //   currentUser?: SafeUser | null;
// // }

// // interface PricingPlan {
// //   name: string;
// //   displayName: string;
// //   price: number;
// //   originalPrice: number;
// //   articles: number;
// //   description: string;
// //   features: string[];
// //   highlight: boolean;
// //   badge?: string;
// // }

// // const pricingPlans: PricingPlan[] = [
// //   {
// //     name: "basic-monthly",
// //     displayName: "Basic",
// //     price: 5.99,
// //     originalPrice: 9,
// //     articles: 20,
// //     description: "Perfect for startups",
// //     features: [
// //       "20 Articles/Month",
// //       "2 Websites",
// //       "SEO Dashboard",
// //       "AI-optimized for Google & AI search",
// //       "Backlink from the SEOZast directory",
// //       "Real-Time Analytics",
// //       "Automated backlinks",
// //       "One-Click GitHub Publishing",
// //     ],
// //     highlight: false,
// //   },
// //   {
// //     name: "pro-monthly",
// //     displayName: "Pro",
// //     price: 9.99,
// //     originalPrice: 19,
// //     articles: 40,
// //     description: "Great for growing sites",
// //     features: [
// //       "40 Articles/Month",
// //       "4 Websites",
// //       "SEO Dashboard",
// //       "AI-optimized for Google & AI search",
// //       "Backlink from the SEOZast directory",
// //       "Real-Time Analytics",
// //       "Automated backlinks",
// //       "One-Click GitHub Publishing",
// //       "Priority Support",
// //     ],
// //     highlight: true,
// //     badge: "Most Popular",
// //   },
// //   {
// //     name: "enterprise-monthly",
// //     displayName: "Enterprise",
// //     price: 29.99,
// //     originalPrice: 49,
// //     articles: 100,
// //     description: "Built for high-traffic sites",
// //     features: [
// //       "100 Articles/Month",
// //       "6 Websites",
// //       "SEO Dashboard",
// //       "AI-optimized for Google & AI search",
// //       "Backlink from the SEOZast directory",
// //       "Real-Time Analytics",
// //       "Automated backlinks",
// //       "One-Click GitHub Publishing",
// //       "Priority Support",
// //     ],
// //     highlight: true,
// //     badge: "Best for Scale",
// //   },
// //   {
// //     name: "basic-yearly",
// //     displayName: "Basic",
// //     price: 59.99, // ~17% savings from monthly (5.99 × 12 = 71.88)
// //     originalPrice: 72,
// //     articles: 20,
// //     description: "Perfect for startups",
// //     features: [
// //       "20 Articles/Month",
// //       "2 Websites",
// //       "SEO Dashboard",
// //       "AI-optimized for Google & AI search",
// //       "Backlink from the SEOZast directory",
// //       "Real-Time Analytics",
// //       "Automated backlinks",
// //       "One-Click GitHub Publishing",
// //     ],
// //     highlight: false,
// //   },
// //   {
// //     name: "pro-yearly",
// //     displayName: "Pro",
// //     price: 99.99, // ~17% savings from monthly (9.99 × 12 = 119.88)
// //     originalPrice: 120,
// //     articles: 40,
// //     description: "Great for growing sites",
// //     features: [
// //       "40 Articles/Month",
// //       "4 Websites",
// //       "SEO Dashboard",
// //       "AI-optimized for Google & AI search",
// //       "Backlink from the SEOZast directory",
// //       "Real-Time Analytics",
// //       "Automated backlinks",
// //       "One-Click GitHub Publishing",
// //       "Priority Support",
// //     ],
// //     highlight: true,
// //     badge: "Most Popular",
// //   },
// //   {
// //     name: "enterprise-yearly",
// //     displayName: "Enterprise",
// //     price: 299.99, // ~17% savings from monthly (29.99 × 12 = 359.88)
// //     originalPrice: 360,
// //     articles: 100,
// //     description: "Built for high-traffic sites",
// //     features: [
// //       "100 Articles/Month",
// //       "6 Websites",
// //       "SEO Dashboard",
// //       "AI-optimized for Google & AI search",
// //       "Backlink from the SEOZast directory",
// //       "Real-Time Analytics",
// //       "Automated backlinks",
// //       "One-Click GitHub Publishing",
// //       "Priority Support",
// //     ],
// //     highlight: true,
// //     badge: "Best for Scale",
// //   },
// // ];

// // const PricingSection: React.FC<PricingProps> = ({ currentUser }) => {
// //   const { data: session } = useSession();
// //   const router = useRouter();
// //   const [isYearly, setIsYearly] = useState(true);
// //   const [loading, setLoading] = useState(false);
// //   const [couponCode, setCouponCode] = useState(""); // Estado global para el código de cupón
// //   const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; valid: boolean; message?: string; discountType?: string; discountValue?: number; } | null>(null); // Estado para cupón aplicado

// //   const handlePlanSelection = async (planName: string) => {
// //     const planKey = `${planName.toLowerCase()}${isYearly ? "-yearly" : "-monthly"}`;
// //     // console.log("planKey", planKey);

// //     if (!session) {
// //       router.push(`/register?plan=${planName.toLowerCase()}&yearly=${isYearly}`);
// //     } else {
// //       await handleBecomePremium(planKey);
// //     }
// //   };

// //   const handleBecomePremium = async (selectedPlan: string) => {
// //     if (!session?.user.id) {
// //       toast.error("User not identified");
// //       return;
// //     }

// //     setLoading(true);
// //     try {
// //       // Envía el couponCode si está aplicado y válido
// //       const payload: { plan: string; couponCode?: string } = { plan: selectedPlan };
// //       if (appliedCoupon?.valid && couponCode) {
// //         payload.couponCode = couponCode;
// //       }

// //       const response = await axios.post("/api/stripe/subscribe", payload);
// //       if (response.data.url) {
// //         toast.success("Redirecting to payment page...");
// //         setTimeout(() => {
// //           window.location.href = response.data.url;
// //         }, 1000);
// //       } else {
// //         toast.success(response.data.message);
// //         router.refresh();
// //       }
// //     } catch (error: any) {
// //       toast.error(error.response?.data?.error || "Error initiating subscription");
// //       console.error(error);
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const handleApplyCoupon = async () => {
// //     if (!couponCode.trim()) {
// //       toast.error("Enter a coupon code");
// //       return;
// //     }

// //     setLoading(true);
// //     try {
// //       // Opcional: Valida el cupón en el backend para seguridad (evita spoofing)
// //       // Aquí solo validamos localmente, pero el backend lo validará de nuevo
// //       const response = await axios.post("/api/coupons/validate", { code: couponCode }); // Crea este endpoint si quieres validar aquí
// //       if (response.data.valid) {
// //         setAppliedCoupon({ code: couponCode, valid: true, message: response.data.message || "Coupon applied!" });
// //         toast.success("Coupon applied successfully!");
// //       } else {
// //         setAppliedCoupon({ code: couponCode, valid: false, message: response.data.error });
// //         toast.error(response.data.error || "Invalid coupon");
// //       }
// //     } catch (error: any) {
// //       setAppliedCoupon({ code: couponCode, valid: false });
// //       toast.error(error.response?.data?.error || "Failed to validate coupon");
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const handleRemoveCoupon = () => {
// //     setCouponCode("");
// //     setAppliedCoupon(null);
// //     toast.success("Coupon removed");
// //   };

// //   const formatPrice = (price: number) => {
// //     const dollars = Math.floor(price);
// //     const cents = Math.round((price - dollars) * 100);
// //     return (
// //       <>
// //         {dollars}
// //         <span className="text-lg align-top">.{cents.toString().padStart(2, '0')}</span>
// //       </>
// //     );
// //   };

// //   const getDisplayPrice = (plan: PricingPlan): JSX.Element => {

// //     let displayElements: JSX.Element[] = [];

// //     if (appliedCoupon?.valid) {

// //       let discountedPrice = plan.price;
// //       let discountType = appliedCoupon.discountType || "PERCENTAGE"; // Fallback
// //       let discountValue: number = appliedCoupon.discountValue ? Number(appliedCoupon.discountValue) : 0; // Asegura number

// //       // Parsear desde message si falta data numérica
// //       if (!appliedCoupon.discountValue) {
// //         const percentMatch = appliedCoupon.message?.match(/(\d+)% off/);
// //         const fixedMatch = appliedCoupon.message?.match(/(\d+) USD off/); // Ajusta si fixed
// //         if (percentMatch) {
// //           discountType = "PERCENTAGE";
// //           discountValue = parseInt(percentMatch[1], 10);
// //         } else if (fixedMatch) {
// //           discountType = "FIXED";
// //           discountValue = parseInt(fixedMatch[1], 10);
// //         } else {
// //           console.warn("[getDisplayPrice] Could not parse discount from message");
// //         }
// //       }

// //       if (discountType === "PERCENTAGE" && discountValue > 0) {
// //         discountedPrice = plan.price * (1 - discountValue / 100);
// //       } else if (discountType === "FIXED" && discountValue > 0) {
// //         discountedPrice = Math.max(0, plan.price - discountValue);
// //       }

// //       displayElements.push(
// //         <s key="original" className="text-xl text-gray-500 mr-2">
// //           ${formatPrice(plan.price)}
// //         </s>
// //       );
// //       displayElements.push(
// //         <span key="discounted" className="text-emerald-600">
// //           ${formatPrice(discountedPrice)}
// //         </span>
// //       );
// //     } else {
// //       displayElements.push(
// //         <span key="normal">${formatPrice(plan.price)}</span>
// //       );
// //     }

// //     displayElements.push(
// //       <span key="suffix" className="text-base font-normal text-gray-600">
// //         {isYearly ? "/yr" : "/mo"}
// //       </span>
// //     );

// //     return <>{displayElements}</>;
// //   };

// //   return (
// //     <section id="pricing" className="bg-gray-100 py-10 sm:py-16">
// //       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
// //         <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-8">
// //           Start Growing Your Traffic Today
// //         </h2>
// //         <h4 className="text-xl md:text-lg text-black font-medium text-center mb-8">
// //           Everything you need for SEO and AI search — 90% cheaper than Surfer or Jasper.
// //         </h4>
// //         {/* Toggle Section */}
// //         <div className="flex justify-center mb-12">
// //           <div className="flex w-full sm:w-auto bg-white rounded-lg shadow-sm border">
// //             <button
// //               onClick={() => setIsYearly(false)}
// //               className={`flex-1 px-4 sm:px-6 py-3 text-sm sm:text-base font-medium transition-all focus:outline-none focus:ring-2 focus:ring-emerald-600 rounded-l-lg ${!isYearly
// //                 ? "bg-emerald-600 text-white shadow-md"
// //                 : "bg-white text-gray-700 hover:bg-gray-50"
// //                 }`}
// //             >
// //               Monthly
// //             </button>
// //             <button
// //               onClick={() => setIsYearly(true)}
// //               className={`flex-1 px-4 sm:px-6 py-3 text-sm sm:text-base font-medium transition-all focus:outline-none focus:ring-2 focus:ring-emerald-600 relative rounded-r-lg ${isYearly
// //                 ? "bg-emerald-600 text-white shadow-md"
// //                 : "bg-white text-gray-700 hover:bg-gray-50"
// //                 }`}
// //             >
// //               Yearly
// //               <span className="absolute -top-3 -right-2 bg-yellow-400 text-black text-[10px] font-bold px-2 py-1 rounded-full shadow-sm z-10">
// //                 Save 17%
// //               </span>
// //             </button>
// //           </div>
// //         </div>

// //         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
// //           {pricingPlans
// //             .filter((plan) =>
// //               isYearly ? plan.name.includes("-yearly") : plan.name.includes("-monthly")
// //             )
// //             .map((plan) => (
// //               <div
// //                 key={plan.name}
// //                 className={`relative bg-white p-6 rounded-lg border shadow-md text-center flex flex-col min-h-[400px] transition-all duration-300 ${plan.displayName === "Pro"
// //                   ? "border-2 border-emerald-600"
// //                   : plan.highlight
// //                     ? "border-gray-200 bg-emerald-50"
// //                     : "border-gray-200"
// //                   } ${loading ? "opacity-70 cursor-not-allowed" : "hover:shadow-lg"}`}
// //               >
// //                 {plan.badge && (
// //                   <div className="absolute top-0 left-0 right-0 flex justify-center -translate-y-1/2 z-30">
// //                     <span className="text-xs font-bold px-3 py-1 rounded-full shadow-sm bg-emerald-100 text-emerald-500">
// //                       {plan.badge}
// //                     </span>
// //                   </div>
// //                 )}
// //                 {isYearly && (
// //                   <span className="absolute top-6 right-2 bg-yellow-300 text-black text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm z-20 flex items-center">
// //                     <Coffee className="w-3 h-3 mr-1" />
// //                     Save ${plan.name === "basic-yearly" && "12"}{plan.name === "pro-yearly" && "20"}{plan.name === "enterprise-yearly" && "60"}
// //                   </span>
// //                 )}
// //                 <h3
// //                   className={`text-xl font-semibold text-gray-800 mb-3 pt-4 ${plan.badge ? "mt-6" : ""
// //                     }`}
// //                 >
// //                   {plan.displayName}
// //                 </h3>
// //                 <div className="text-3xl font-bold text-gray-800 mb-2">
// //                   {isYearly && !appliedCoupon?.valid && (
// //                     <s className="text-xl text-gray-500 mr-2">${plan.originalPrice}</s>
// //                   )}
// //                   {appliedCoupon?.valid ? (
// //                     <span className="text-black text-2xl">{getDisplayPrice(plan)}</span>
// //                   ) : (
// //                     <>
// //                       ${formatPrice(plan.price)}
// //                       <span className="text-base font-normal text-gray-600">
// //                         {isYearly ? "/yr" : "/mo"}
// //                       </span>
// //                     </>
// //                   )}
// //                 </div>
// //                 <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
// //                 <ul className="text-left text-base text-gray-700 space-y-2 mb-6 flex-grow">
// //                   {plan.features.map((feature, idx) => (
// //                     <li key={idx} className="flex items-start">
// //                       <span className="text-emerald-600 mr-2">✔</span>
// //                       <div>
// //                         <span>{feature}</span>
// //                         {/* <span className={feature === "Real-Time Analytics" ? "font-bold text-emerald-600" : ""}>
// //                           {feature}
// //                         </span> */}
// //                         {feature === "Automated backlinks" && (
// //                           <p className="text-xs text-gray-500 ml-2">
// //                             (from your published articles on your site)
// //                           </p>
// //                         )}
// //                       </div>
// //                     </li>
// //                   ))}
// //                 </ul>
// //                 <button
// //                   onClick={() => handlePlanSelection(plan.displayName)}
// //                   disabled={loading}
// //                   className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${loading
// //                     ? "bg-gray-300 text-gray-600 cursor-not-allowed"
// //                     : "bg-emerald-600 text-white hover:bg-emerald-500"
// //                     }`}
// //                 >
// //                   {loading ? "Processing..." : session ? "Subscribe" : "Sign Up"}
// //                 </button>
// //               </div>
// //             ))}
// //         </div>
// //         {/* <p className="text-md text-gray-400 mt-8 text-center">
// //           ⚡ Launch offer: lock this price for lifetime access.
// //           Prices double next week.
// //         </p> */}

// //         {/* Coupon Input Global - Aplica a todos los planes */}
// //         <div className="mt-8">
// //           <p className="sm:text-lg text-md text-center mb-4 font-semibold">Do you have a coupon?</p>
// //           <p className="sm:text-md text-sm text-center mb-4 font-light">Sign up/Log in to apply it</p>

// //           <div className="max-w-md mx-auto mb-8 flex flex-col sm:flex-row gap-2">

// //             <input
// //               type="text"
// //               value={couponCode}
// //               onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
// //               placeholder="Enter coupon code"
// //               className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
// //               disabled={loading}
// //             />
// //             {appliedCoupon?.valid ? (
// //               <button
// //                 onClick={handleRemoveCoupon}
// //                 className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
// //                 disabled={loading}
// //               >
// //                 Remove
// //               </button>
// //             ) : (
// //               <button
// //                 onClick={handleApplyCoupon}
// //                 className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
// //                 disabled={loading}
// //               >
// //                 Apply Coupon
// //               </button>
// //             )}
// //           </div>
// //         </div>
// //         {appliedCoupon && (
// //           <div className="text-center mb-4 text-sm">
// //             {appliedCoupon.valid ? (
// //               <span className="text-emerald-600 font-medium">{appliedCoupon.message || `${appliedCoupon.code} applied!`}</span>
// //             ) : (
// //               <span className="text-red-600">{appliedCoupon.message || "Invalid coupon"}</span>
// //             )}
// //           </div>
// //         )}

// //         <div className="mt-16 text-center">
// //           <div className="inline-block bg-emerald-100 text-black font-medium sm:text-lg text-md px-4 py-2 rounded-lg shadow-md transition-transform duration-300 hover:scale-105 border border-emerald-300">
// //             Pair with{' '}
// //             <Link
// //               href="https://peerquik.com"
// //               target="_blank"
// //               rel="noopener noreferrer"
// //               className="text-emerald-500 hover:underline font-medium"
// //             >
// //               PeerQuik
// //             </Link>{' '}
// //             for rapid B2B/B2C launches.
// //           </div>
// //         </div>
// //       </div>
// //       <style jsx>{`
// //         @keyframes fadeIn {
// //           from {
// //             opacity: 0;
// //           }
// //           to
// //             opacity: 1;
// //           }
// //         }
// //         .animate-fadeIn {
// //           animation: fadeIn 0.3s ease-in forwards;
// //         }
// //       `}</style>
// //     </section>
// //   );
// // };

// // export default PricingSection;